import React, { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import {
	FormControlLabel,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Skeleton,
	SvgIcon,
	Tooltip,
	Typography,
	Paper,
} from "@mui/material";
import {
	CoreButton,
	CoreCheckbox,
	CoreDatePicker,
	CoreDurationPicker,
	CoreErrorIndicator,
	CoreExpandButton,
	CoreModal,
	CorePaper,
	CoreRadio,
	CoreTimePicker,
	CoreTextField,
	CoreSelectTextField,
	CoreMenuButton,
	CoreMenuItem,
	CoreChip,
	CoreErrorIndicatorRow,
	CoreFocusTrap,
} from "../../core";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import i18nPackage from "../../i18n/i18n";
import { LiveSession } from "../../api/fetch";
import {
	areLiveSessionRegistrationTimesChanged,
	areLiveSessionTimesChanged,
	checkProctorGroupCancellationTime,
	checkProctorGroupNotice,
	getPotentiallyCancelledInvitations,
	isLiveSessionCapacityChanged,
	isLiveSessionDemoMode,
	isLiveSessionNameChanged,
	isLiveSessionProctorChanged,
	isLiveSessionProctorGroupChanged,
	LiveSessionWithInvitations,
	PendingLiveSession,
	ProctorGroupInfo,
	ProctorInfo,
	resetStartedLiveSessions,
	spansMultipleDays,
} from "../liveHelper";
import { changeTimeOnly } from "../../core/datetime-picker/TimePicker";
import styles from "./live-sessions-step.module.css";
import Add from "../images/add.svg?react";
import { useShadowContainer } from "../shadow-dom-context";

type PendingLiveSessionUpdate = Pick<
	PendingLiveSession,
	"name" | "startTime" | "endTime" | "capacity" | "registrationStartTime" | "registrationEndTime"
>;

const dateTimeHeight = "36px";

function changeDateOnly(oldDateTime: Date, newDate: Date) {
	const newDateTime = new Date(newDate.getTime());
	newDateTime.setHours(oldDateTime.getHours());
	newDateTime.setMinutes(oldDateTime.getMinutes());
	newDateTime.setSeconds(oldDateTime.getSeconds(), oldDateTime.getMilliseconds());
	return newDateTime;
}

function stripSeconds(timestamp: number) {
	const newDate = new Date(timestamp);
	newDate.setSeconds(0, 0);
	return newDate;
}

function SessionInfoHeader({ session }: { session: PendingLiveSession }) {
	const { name, startTime, endTime, capacity } = session;
	const isMobileLayout = useMobileLayout();

	const formatShortDate = (date: Date) => {
		return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
	};

	const formatTime = (time: Date) => {
		return time.toLocaleTimeString([], { timeStyle: "short" });
	};

	const totalMinutes = Math.trunc((endTime.getTime() - startTime.getTime()) / (60 * 1000));
	const totalHours = Math.trunc(totalMinutes / 60);
	const days = Math.trunc(totalHours / 24);
	const hours = Math.trunc(totalHours % 24);
	const minutes = totalMinutes % 60;
	const unitsAndValues: [string, number][] = [
		["Day", days],
		["Hour", hours],
		["Minute", minutes],
	];
	const multipleDates = spansMultipleDays(startTime, endTime);
	return (
		<div className={styles.sessionInfoHeader}>
			<div className={styles.sessionName}>{name}</div>
			<div className={`${styles.sessionDetails} ${isMobileLayout ? styles.sessionDetailsCompact : ""}`}>
				<div className={`${styles.sessionDetail} ${isMobileLayout ? styles.sessionDetailCompact : ""}`}>
					{multipleDates
						? `${formatShortDate(startTime)}, at ${formatTime(startTime)}–${formatShortDate(endTime)}, at ${formatTime(endTime)}`
						: startTime.toLocaleDateString([], { dateStyle: "full" })}
				</div>
				{!multipleDates && (
					<div className={`${styles.sessionDetail} ${isMobileLayout ? styles.sessionDetailCompact : ""}`}>
						{formatTime(startTime)}–{formatTime(endTime)}
					</div>
				)}
				<div className={`${styles.sessionDetail} ${isMobileLayout ? styles.sessionDetailCompact : ""}`}>
					{capacity} Capacity
				</div>
				<div className={`${styles.sessionDetail} ${isMobileLayout ? styles.sessionDetailCompact : ""}`}>
					{unitsAndValues
						.filter(([, value]) => value > 0)
						.map(([unitName, value]) => `${value} ${unitName}${value === 1 ? "" : "s"}`)
						.join(" ")}
				</div>
			</div>
		</div>
	);
}

function VerticalLabel({ label, control, error }: { label: ReactNode; control: ReactElement; error?: ReactNode }) {
	return (
		<FormControlLabel
			className={styles.verticalTextFieldLabel}
			control={control}
			label={
				<div className={styles.verticalTextFieldLabelErrorRow}>
					{label}
					<CoreErrorIndicator error={error} size={18} />
				</div>
			}
			labelPlacement="top"
		/>
	);
}

function SessionEditor({
	session,
	existingSession,
	onChange,
}: {
	session: PendingLiveSession;
	existingSession?: LiveSessionWithInvitations;
	onChange: (session: PendingLiveSession) => void;
}) {
	const currTime = new Date();
	const { startTime, endTime, capacity } = session;
	const isMobileLayout = useMobileLayout();

	const demoMode = isLiveSessionDemoMode(session);

	const isStarted = !!existingSession && currTime >= new Date(existingSession.startTime);

	const timesChanged = !existingSession || areLiveSessionTimesChanged(existingSession, session);
	const startDatePastError =
		timesChanged && currTime >= changeTimeOnly(new Date(startTime.getTime() + 24 * 60 * 60 * 1000), 0, 0);
	const latestEndDateTime = changeTimeOnly(new Date(endTime.getTime() + 24 * 60 * 60 * 1000), 0, 0);
	const endDatePastError = timesChanged && currTime >= latestEndDateTime;
	const earliestStartDateTime = changeTimeOnly(startTime, 0, 0);
	const dateOrderError = timesChanged && earliestStartDateTime >= latestEndDateTime;
	const startTimePastError = timesChanged && currTime >= startTime;
	const endTimePastError = timesChanged && currTime >= endTime;
	const timeOrderError = timesChanged && startTime >= endTime;
	const durationError = timesChanged && !demoMode && endTime.getTime() - startTime.getTime() >= 24 * 60 * 60 * 1000;

	const capacityError = (!existingSession || isLiveSessionCapacityChanged(existingSession, session)) && capacity <= 0;

	return (
		<div className={styles.section}>
			<Typography variant="h4" className={styles.sectionHeader}>
				Select Date and Time
			</Typography>
			<div className={`${styles.sectionContent} ${isMobileLayout ? styles.sectionContentCompact : ""}`}>
				<VerticalLabel
					control={
						<CoreDatePicker
							disablePast
							value={startTime}
							onChange={(date) => {
								if (date) {
									const newStartTime = changeDateOnly(startTime, date);
									onChange({
										...session,
										startTime: newStartTime,
										endTime: demoMode
											? new Date(newStartTime.getTime() + (endTime.getTime() - startTime.getTime()))
											: changeDateOnly(endTime, date),
									});
								}
							}}
							disabled={isStarted}
							error={startDatePastError || (demoMode && dateOrderError)}
							dialogAriaLabel={i18nPackage.t(
								"editQuizPage.liveSessionsStep.liveSession.ariaLabels.startDateLiveSessionDialog",
							)}
							slotProps={{
								textField: {
									disabledTooltip: isStarted
										? demoMode
											? "The Start Date cannot be edited because the session has started."
											: "The Date cannot be edited because the session has started."
										: undefined,
									sx: {
										".MuiInputBase-input": {
											fontSize: 15,
										},
										"& .MuiInputBase-root": {
											height: dateTimeHeight,
										},
									},
								},
							}}
						/>
					}
					label={demoMode ? "Start Date" : "Select Date"}
					error={
						startDatePastError
							? demoMode
								? "Select a Start Date in the future."
								: "Select a Date in the future."
							: dateOrderError
								? "Select an Start Date that is before the End Date."
								: undefined
					}
				/>
				<VerticalLabel
					control={
						<CoreTimePicker
							fontSize="15px"
							value={startTime}
							onChange={(time) => {
								if (time) {
									onChange({
										...session,
										startTime: time,
										endTime: new Date(time.getTime() + (endTime.getTime() - startTime.getTime())),
									});
								}
							}}
							disabled={isStarted}
							disabledTooltip={
								isStarted ? "The Start Time cannot be edited because the session has started." : undefined
							}
							error={startTimePastError || timeOrderError}
							sx={{ "& .MuiOutlinedInput-root": { height: dateTimeHeight } }}
						/>
					}
					label="Start Time"
					error={
						startTimePastError
							? "Select a Start Time in the future."
							: timeOrderError
								? "Select a Start Time that is before the End Time."
								: undefined
					}
				/>
				{demoMode && (
					<VerticalLabel
						control={
							<CoreDatePicker
								disablePast
								value={endTime}
								onChange={(date) => {
									if (date) {
										onChange({
											...session,
											endTime: changeDateOnly(endTime, date),
										});
									}
								}}
								disabled={isStarted}
								error={endDatePastError || dateOrderError}
								dialogAriaLabel={i18nPackage.t(
									"editQuizPage.liveSessionsStep.liveSession.ariaLabels.endDateLiveSessionDialog",
								)}
								slotProps={{
									textField: {
										disabledTooltip: isStarted
											? "The End Date cannot be edited because the session has started."
											: undefined,
										sx: {
											".MuiInputBase-input": {
												fontSize: 15,
											},
											"& .MuiInputBase-root": {
												height: dateTimeHeight,
											},
										},
									},
								}}
							/>
						}
						label="End Date"
						error={
							endDatePastError
								? "Select an End Date in the future."
								: dateOrderError
									? "Select an End Date that is after the Start Date."
									: undefined
						}
					/>
				)}
				<VerticalLabel
					control={
						<CoreTimePicker
							fontSize="15px"
							value={endTime}
							onChange={(time) => {
								if (time) {
									onChange({
										...session,
										endTime: time,
									});
								}
							}}
							disabled={isStarted}
							disabledTooltip={isStarted ? "The End Time cannot be edited because the session has started." : undefined}
							error={endTimePastError || timeOrderError || durationError}
							sx={{ "& .MuiOutlinedInput-root": { height: dateTimeHeight } }}
						/>
					}
					label="End Time"
					error={
						endTimePastError
							? "Select an End Time in the future."
							: timeOrderError
								? "Select an End Time that is after the Start Time."
								: durationError
									? "Select an End Time that is less than 24 hours after the Start time."
									: undefined
					}
				/>
				<VerticalLabel
					control={
						<CoreDurationPicker
							fontSize="15px"
							units={demoMode ? { days: true, minutes: true, hours: true } : undefined}
							value={endTime.getTime() - startTime.getTime()}
							onChange={(duration) => {
								if (duration) {
									onChange({
										...session,
										endTime: new Date(startTime.getTime() + duration),
									});
								}
							}}
							minValue={60 * 1000}
							disabled={isStarted}
							disabledTooltip={isStarted ? "The Duration cannot be edited because the session has started." : undefined}
							error={timeOrderError || durationError}
							sx={{ "& .MuiOutlinedInput-root": { height: dateTimeHeight } }}
						/>
					}
					label="Set Duration"
					error={
						timeOrderError
							? "Select a Duration that is at least 1 minute."
							: durationError
								? "Select a Duration that is less than 24 hours."
								: undefined
					}
				/>
				<VerticalLabel
					control={
						<CoreTextField
							sx={{ ".MuiInputBase-input": { fontSize: "15px" } }}
							type="number"
							value={capacity}
							onChange={(e) => onChange({ ...session, capacity: Number(e.target.value) })}
							disabled={isStarted}
							disabledTooltip={isStarted ? "The Capacity cannot be edited because the session has started." : undefined}
							InputProps={{
								inputProps: { min: 1 },
								sx: { height: dateTimeHeight },
							}}
							error={capacityError}
						/>
					}
					label="Set Capacity"
					error={capacityError && "Enter a Capacity of at least 1."}
				/>
			</div>
		</div>
	);
}

function ProctorOption({
	checked,
	onChange,
	disabled = false,
	disabledTooltip,
	primary,
	secondary,
	control,
}: {
	checked: boolean;
	onChange: (checked: boolean) => void;
	disabled?: boolean;
	disabledTooltip?: ReactNode;
	primary: ReactNode;
	secondary: ReactNode;
	control: ReactNode;
}) {
	const isMobileLayout = useMobileLayout();

	function focusAndClickFirstDescendant(element: Element) {
		if (!(element instanceof HTMLElement)) return;
		const focusableSelectors = [
			"button",
			"a[href]",
			"input:not([disabled]):not([type='hidden'])",
			"select:not([disabled])",
			"textarea:not([disabled])",
			"[tabindex]:not([tabindex='-1'])",
		];

		const focusableChild = element.querySelector<HTMLElement>(focusableSelectors.join(","));

		if (focusableChild) {
			focusableChild.focus();
			focusableChild.click();
		} else {
			element.focus();
			element.click();
		}
	}

	const radioRef = useRef<HTMLButtonElement | null>(null);
	const handleControlKeyDown = (event: React.KeyboardEvent) => {
		const dropDownExpanded = event.currentTarget.querySelector('div[role="combobox"][aria-expanded="true"]');
		if (dropDownExpanded) return;

		// if the focus is on the control component and horizontal arrow keys are pressed, move to the next radio button accordingly
		if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
			const list = event.currentTarget.closest("ul.MuiList-root");
			if (!list) return;
			const radios = Array.from(list.querySelectorAll(".MuiRadio-root"));
			const currentIndex = radios.findIndex((radio) => radio === radioRef.current);

			if (currentIndex !== -1) {
				const nextIndex = currentIndex === 0 ? 1 : 0;
				if (radios[nextIndex]) {
					focusAndClickFirstDescendant(radios[nextIndex]);
				}
			}
		}
	};

	const radio = (
		<CoreRadio checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} ref={radioRef} />
	);

	const proctorOption = (
		<ListItem className={`${styles.proctorOption} ${isMobileLayout ? styles.proctorOptionCompact : ""}`}>
			<div style={{ display: "flex" }}>
				<ListItemIcon className={styles.radioWrapper}>{radio}</ListItemIcon>
				<ListItemText
					className={`${styles.proctorOptionText} ${disabled ? styles.proctorOptionTextDisabled : ""}`}
					primary={primary}
					secondary={
						isMobileLayout ? (
							<span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{secondary}</span>
						) : (
							secondary
						)
					}
					sx={{
						margin: 0, // Override default Mui margin. css got overriden by mui.
						maxWidth: isMobileLayout ? "230px" : undefined,
					}}
				/>
			</div>
			{checked &&
				React.cloneElement(control as React.ReactElement, {
					onKeyDown: handleControlKeyDown, // attach the onKeyDown handler to the control component passed from props
				})}
		</ListItem>
	);
	if (!disabled) return proctorOption;
	return <Tooltip title={disabledTooltip}>{proctorOption}</Tooltip>;
}

function ProctorEditor({
	availableProctors,
	availableProctorGroups,
	session,
	existingSession,
	onChange,
}: {
	availableProctors: Record<string, ProctorInfo>;
	availableProctorGroups: Record<number, ProctorGroupInfo>;
	session: PendingLiveSession;
	existingSession?: LiveSessionWithInvitations;
	onChange: (session: PendingLiveSession) => void;
}) {
	const { proctorId, proctorGroupId } = session;
	const currTime = new Date();
	const shadowDomContainer = useShadowContainer();
	const isMobileLayout = useMobileLayout();

	const [mode, setMode] = useState<"proctor" | "group">(proctorGroupId ? "group" : "proctor");
	useEffect(() => {
		setMode(proctorGroupId ? "group" : "proctor");
	}, [proctorGroupId]);

	const proctorChanged =
		!existingSession ||
		isLiveSessionProctorChanged(existingSession, session) ||
		isLiveSessionProctorGroupChanged(existingSession, session);
	const noProctorError = proctorChanged && !proctorId && !proctorGroupId;

	const selectedProctorGroup = proctorGroupId ? availableProctorGroups[proctorGroupId] : undefined;
	const proctorGroupNoticeError =
		proctorChanged && selectedProctorGroup && !checkProctorGroupNotice(session, selectedProctorGroup);

	const isStarted = !!existingSession && currTime >= new Date(existingSession.startTime);

	const setSessionProctorGroup = (proctorGroupId: number) => {
		const updatedSession = { ...session, proctorId: undefined, proctorGroupId };
		const proctorGroup = availableProctorGroups[proctorGroupId];
		if (
			proctorGroup?.autoCancelEmptyHoursBefore != null &&
			!checkProctorGroupCancellationTime(updatedSession, proctorGroup)
		) {
			updatedSession.registrationEndTime = new Date(
				updatedSession.startTime.getTime() - proctorGroup.autoCancelEmptyHoursBefore * 60 * 60 * 1000,
			);
			if (!updatedSession.registrationStartTime) {
				const currTimestamp = Date.now();
				updatedSession.registrationStartTime = new Date(
					Math.min(currTimestamp + 60 * 60 * 1000, updatedSession.startTime.getTime() - 60 * 1000),
				);
			}
		}
		return updatedSession;
	};

	return (
		<div className={styles.section}>
			<Typography variant="h4" className={styles.sectionHeader}>
				{i18nPackage.t("editQuizPage.liveSessionsStep.proctor.title")}
			</Typography>
			{i18nPackage.t("editQuizPage.liveSessionsStep.proctor.description")}
			<List className={styles.proctorOptions}>
				<ProctorOption
					checked={mode === "proctor"}
					onChange={(checked) => {
						setMode(checked ? "proctor" : "group");
						onChange({
							...session,
							proctorId: Object.values(availableProctors)[0]?.id,
							proctorGroupId: undefined,
						});
					}}
					primary="Individual Proctor"
					secondary="Select a proctor for the session."
					disabled={isStarted && existingSession && existingSession.invitations.length > 0}
					disabledTooltip={
						isStarted ? i18nPackage.t("editQuizPage.liveSessionsStep.proctor.sessionStartedNotEditable") : undefined
					}
					control={
						<CoreErrorIndicatorRow
							error={noProctorError ? i18nPackage.t("editQuizPage.liveSessionsStep.proctor.noProctor") : undefined}
						>
							<CoreSelectTextField<string>
								className={`${styles.proctorDropdown} ${isMobileLayout ? styles.proctorDropdownCompact : ""}`}
								sx={{ ".MuiInputBase-input": { fontSize: "15px" } }}
								value={proctorId ?? null}
								onChange={(e) => onChange({ ...session, proctorId: e.target.value })}
								label={i18nPackage.t("editQuizPage.liveSessionsStep.proctor.selectProctor")}
								renderValue={(id) => availableProctors[id]?.name}
								error={noProctorError}
								shadowDomContainer={shadowDomContainer}
							>
								{Object.values(availableProctors).map(({ id, name }) => (
									<CoreMenuItem key={id} value={id} sx={{ fontSize: "15px" }}>
										{name || "[No name]"}
									</CoreMenuItem>
								))}
							</CoreSelectTextField>
						</CoreErrorIndicatorRow>
					}
				/>
				<ProctorOption
					disabled={Object.keys(availableProctorGroups).length === 0}
					disabledTooltip={i18nPackage.t("editQuizPage.liveSessionsStep.proctor.noGroupProctorsTooltip")}
					checked={mode === "group"}
					onChange={(checked) => {
						setMode(checked ? "group" : "proctor");
						const firstProctorGroupId = Object.values(availableProctorGroups).find((group) =>
							checkProctorGroupNotice(session, group),
						)?.id;
						onChange({
							...session,
							proctorId: undefined,
							...(firstProctorGroupId ? setSessionProctorGroup(firstProctorGroupId) : {}),
						});
					}}
					primary={i18nPackage.t("editQuizPage.liveSessionsStep.proctor.proctoringGroupTitle")}
					secondary={i18nPackage.t("editQuizPage.liveSessionsStep.proctor.proctoringGroupDescription")}
					control={
						<CoreErrorIndicatorRow
							error={
								noProctorError
									? i18nPackage.t("editQuizPage.liveSessionsStep.proctor.noProctor")
									: proctorGroupNoticeError && selectedProctorGroup
										? i18nPackage.t("editQuizPage.liveSessionsStep.proctor.insufficientNotice", {
												count: selectedProctorGroup.minHoursNotice,
											})
										: undefined
							}
						>
							<CoreSelectTextField<number>
								className={`${styles.proctorDropdown} ${isMobileLayout ? styles.proctorDropdownCompact : ""}`}
								value={proctorGroupId ?? null}
								onChange={(e) => onChange(setSessionProctorGroup(Number(e.target.value)))}
								disabled={isStarted}
								disabledTooltip={
									isStarted
										? i18nPackage.t("editQuizPage.liveSessionsStep.proctor.sessionStartedNotEditable")
										: undefined
								}
								label={i18nPackage.t("editQuizPage.liveSessionsStep.proctor.selectGroup")}
								renderValue={(id) => availableProctorGroups[id]?.name}
								error={noProctorError || (proctorGroupNoticeError && !!selectedProctorGroup)}
								shadowDomContainer={shadowDomContainer}
							>
								{Object.values(availableProctorGroups).map((proctorGroup) => {
									const { id, name, minHoursNotice } = proctorGroup;
									const insufficientNotice =
										!checkProctorGroupNotice(session, proctorGroup) &&
										!existingSession?.invitations.some(({ proctorGroupId }) => proctorGroupId === id);
									return (
										<CoreMenuItem
											className={styles.dropdownItem}
											key={id}
											value={id}
											disabled={insufficientNotice}
											disabledTooltip={i18nPackage.t("editQuizPage.liveSessionsStep.proctor.insufficientNotice", {
												count: minHoursNotice,
											})}
											slotProps={{
												tooltip: {
													placement: "right",
													slotProps: { tooltip: { sx: { maxWidth: 200 } } },
												},
											}}
										>
											{name}
											<CoreChip chipColor="#DBE7E1" className={styles.minNoticeBadge}>
												{insufficientNotice
													? i18nPackage.t("editQuizPage.liveSessionsStep.proctor.unavailable")
													: i18nPackage.t("editQuizPage.liveSessionsStep.proctor.available")}
											</CoreChip>
										</CoreMenuItem>
									);
								})}
							</CoreSelectTextField>
						</CoreErrorIndicatorRow>
					}
				/>
			</List>
		</div>
	);
}

function ProctorGuideEditor({
	session,
	onChange,
}: {
	session: PendingLiveSession;
	onChange: (session: PendingLiveSession) => void;
}) {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [isIframeLoading, setIsIframeLoading] = useState(true);
	const shadowRoot = useShadowContainer(); // For tabbing in and out of iframe when in the shadow dom. Remove when we migrate to iframe
	const focusableSelector = [
		"a[href]",
		"button:not([disabled])",
		"textarea:not([disabled])",
		"input:not([disabled])",
		"select:not([disabled])",
		"iframe",
		'[tabindex]:not([tabindex="-1"])',
	].join(",");

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			const { action, proctorGuide } = event.data;

			if (event.source !== iframeRef.current?.contentWindow) return;

			if (action === "proctorGuideLoaded") {
				setIsIframeLoading(false);
			}

			if (iframeRef.current && action === "editorReady") {
				iframeRef.current.contentWindow?.postMessage(
					{ action: "loadProctorGuide", proctorGuide: session.proctorGuide },
					"*",
				);
			}

			if (action === "updateProctorGuide") {
				onChange({ ...session, proctorGuide: proctorGuide });
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [session, onChange, iframeRef]);

	/**
	 * So I tried using action messaging from the iframe to the shadow dom for focus management.
	 * But whenever the shadow dom received the message from the iframe, and then attempted to focus the correct element, focus would not stick/work.
	 * I tried logging which element was focused, and it logged the correct element. But visually, there was no focus, and if I pressed tab, focus
	 * would jump to first element in the modal (Step 1 of the Stepper). It seems like focus jumped to the shadow root for some reason???
	 * This issue only happened in shadow dom, so once we switch to iframe, can remove these focus traps and use action messaging.
	 */
	type PlacementRelativeToIframe = "after" | "before";

	// Handles focus when coming out of iframe
	function handleIframeRedirectFocus(dir: PlacementRelativeToIframe) {
		const root = shadowRoot ?? document;
		const allFocusable = Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(
			(el) => el.offsetParent !== null,
		);
		const iframeIndex = allFocusable.indexOf(iframeRef.current!);

		// +-3 = iframe + quill-remove + trap1 + trap2
		// Note no clue what quill-remove is. Seems like a class injected by quill-react
		const focusableIndex = dir === "after" ? iframeIndex + 3 : iframeIndex - 3;
		const focusableElement = allFocusable[focusableIndex];

		if (focusableElement) requestAnimationFrame(() => focusableElement.focus());
	}

	// Handles focus when going into iframe
	function handleIframeFocusInside(dir: PlacementRelativeToIframe) {
		const iframe = iframeRef.current;
		if (!iframe?.contentWindow) return;
		const actionName = dir === "before" ? "focusFirstinEditor" : "focusLastinEditor";
		// Tell iframe to focus first/last element
		iframe.contentWindow.postMessage({ action: actionName }, "*");
	}

	return (
		<>
			<Typography variant="h4" className={styles.sectionHeader}>
				{i18nPackage.t("editQuizPage.liveSessionsStep.proctoringGuidelinesTitle")}
			</Typography>
			<div style={{ display: !isIframeLoading ? "contents" : "none" }}>
				{/* Trap 1: Tab into iframe */}
				<CoreFocusTrap onFocus={() => handleIframeFocusInside("before")} />
				{/* Trap 2: Shift tab from iframe */}
				<CoreFocusTrap onFocus={() => handleIframeRedirectFocus("before")} />
				<iframe
					ref={iframeRef}
					src={
						import.meta.env.VITE_VERITY_ORIGIN + "/edit-quiz-page/quill-editor/index.html" + `#${session.identifier}`
					}
					style={{ display: "block", width: "100%", height: "370px", overflow: "auto", border: "0px" }}
				/>
				{/* Trap 3: Tab out of iframe */}
				<CoreFocusTrap onFocus={() => handleIframeRedirectFocus("after")} />

				{/* Trap 4: Shift+Tab into iframe */}
				<CoreFocusTrap onFocus={() => handleIframeFocusInside("after")} />
			</div>
			{isIframeLoading && <Skeleton variant="rectangular" width="100%" height="370px"></Skeleton>}
		</>
	);
}

function RegistrationWindowEditor({
	availableProctorGroups,
	session,
	existingSession,
	onChange,
}: {
	availableProctorGroups: Record<number, ProctorGroupInfo>;
	session: PendingLiveSession;
	existingSession?: LiveSessionWithInvitations;
	onChange: (session: PendingLiveSession) => void;
}) {
	const currTime = new Date();
	const { startTime, endTime, registrationStartTime, registrationEndTime, proctorGroupId } = session;
	const isMobileLayout = useMobileLayout();

	const timeStarted = currTime >= startTime;

	const sessionTimesChanged = !existingSession || areLiveSessionTimesChanged(existingSession, session);
	const registrationTimesChanged = !existingSession || areLiveSessionRegistrationTimesChanged(existingSession, session);
	const registrationStartDatePastError =
		registrationTimesChanged &&
		registrationStartTime &&
		currTime >= changeTimeOnly(new Date(registrationStartTime.getTime() + 24 * 60 * 60 * 1000), 0, 0);
	const latestRegistrationEndDateTime = registrationEndTime
		? changeTimeOnly(new Date(registrationEndTime.getTime() + 24 * 60 * 60 * 1000), 0, 0)
		: undefined;
	const registrationEndDatePastError =
		registrationTimesChanged && latestRegistrationEndDateTime && currTime >= latestRegistrationEndDateTime;
	const earliestRegistrationStartDateTime = registrationStartTime
		? changeTimeOnly(registrationStartTime, 0, 0)
		: undefined;
	const registrationDateOrderError =
		registrationTimesChanged &&
		earliestRegistrationStartDateTime &&
		latestRegistrationEndDateTime &&
		earliestRegistrationStartDateTime >= latestRegistrationEndDateTime;
	const registrationStartTimePastError =
		registrationTimesChanged && registrationStartTime && currTime >= registrationStartTime;
	const registrationEndTimePastError =
		registrationTimesChanged && registrationEndTime && currTime >= registrationEndTime;
	const registrationTimeOrderError =
		registrationTimesChanged &&
		registrationStartTime &&
		registrationEndTime &&
		registrationStartTime >= registrationEndTime;
	const unfilledRegistrationStartTimeError = registrationEndTime && !registrationStartTime;
	const eitherTimesChanged = registrationTimesChanged || sessionTimesChanged;
	const registrationStartDateAfterStartDateError =
		eitherTimesChanged &&
		earliestRegistrationStartDateTime &&
		!timeStarted &&
		earliestRegistrationStartDateTime >= changeTimeOnly(new Date(startTime.getTime() + 24 * 60 * 60 * 1000), 0, 0);
	const registrationEndDateAfterEndDateError =
		eitherTimesChanged &&
		registrationEndTime &&
		changeTimeOnly(registrationEndTime, 0, 0) >=
			changeTimeOnly(new Date(endTime.getTime() + 24 * 60 * 60 * 1000), 0, 0);
	const registrationStartTimeAfterStartTimeError =
		eitherTimesChanged && registrationStartTime && !timeStarted && registrationStartTime > startTime;
	const registrationEndTimeAfterEndTimeError =
		eitherTimesChanged && registrationEndTime && registrationEndTime > endTime;
	const proctorGroupChanged = !existingSession || isLiveSessionProctorGroupChanged(existingSession, session);
	const proctorGroup = proctorGroupId ? availableProctorGroups[proctorGroupId] : undefined;
	const registrationDateAutoCancellationTimeDifference =
		proctorGroup?.autoCancelEmptyHoursBefore != null && registrationEndTime
			? (changeTimeOnly(registrationEndTime, 0, 0).getTime() -
					changeTimeOnly(
						new Date(startTime.getTime() - proctorGroup.autoCancelEmptyHoursBefore * 60 * 60 * 1000),
						0,
						0,
					).getTime()) /
				(24 * 60 * 60 * 1000)
			: 0;
	const registrationEndDateAfterCancellationDateError =
		(eitherTimesChanged || proctorGroupChanged) &&
		proctorGroup?.autoCancelEmptyHoursBefore != null &&
		(!registrationEndTime || registrationDateAutoCancellationTimeDifference > 0);
	const registrationEndTimeAfterCancellationTimeError =
		(eitherTimesChanged || proctorGroupChanged) &&
		proctorGroup &&
		!checkProctorGroupCancellationTime(session, proctorGroup);
	const unfilledRegistrationEndTimeError =
		!registrationEndTime && (!!registrationStartTime || registrationEndTimeAfterCancellationTimeError);

	return (
		<div className={styles.section}>
			<Typography variant="h4" className={styles.sectionHeader}>
				{i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.title")}
			</Typography>
			{i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.description")}
			<div className={`${styles.sectionContent} ${isMobileLayout ? styles.sectionContentCompact : ""}`}>
				<VerticalLabel
					control={
						<CoreDatePicker
							disablePast
							value={registrationStartTime ?? null}
							onChange={(date) => {
								onChange({
									...session,
									registrationStartTime:
										date && registrationStartTime ? changeDateOnly(registrationStartTime, date) : (date ?? undefined),
									registrationEndTime: !registrationStartTime && date ? endTime : session.registrationEndTime,
								});
							}}
							error={
								registrationStartDatePastError ||
								registrationDateOrderError ||
								registrationStartDateAfterStartDateError ||
								unfilledRegistrationStartTimeError
							}
							dialogAriaLabel={i18nPackage.t(
								"editQuizPage.liveSessionsStep.registrationWindow.ariaLabels.startDateDialog",
							)}
							slotProps={{
								textField: {
									sx: {
										".MuiInputBase-input": {
											fontSize: 15,
										},
										"& .MuiInputBase-root": {
											height: dateTimeHeight,
										},
									},
								},
							}}
						/>
					}
					label={i18nPackage.t("editQuizPage.liveSessionsStep.startDate")}
					error={
						registrationStartDatePastError
							? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.startDatePast")
							: registrationDateOrderError
								? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.startDateAfterEndDate")
								: registrationStartDateAfterStartDateError
									? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.startDateAfterSessionStartDate")
									: unfilledRegistrationStartTimeError
										? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.startDateRequired")
										: undefined
					}
				/>
				<VerticalLabel
					control={
						<CoreTimePicker
							allowEmpty
							fontSize="15px"
							value={registrationStartTime ?? null}
							onChange={(time) => {
								onChange({
									...session,
									registrationStartTime: time,
									registrationEndTime: !registrationStartTime && time ? endTime : session.registrationEndTime,
								});
							}}
							error={
								registrationStartTimePastError ||
								registrationTimeOrderError ||
								registrationStartTimeAfterStartTimeError ||
								unfilledRegistrationStartTimeError
							}
							sx={{ "& .MuiOutlinedInput-root": { height: dateTimeHeight } }}
						/>
					}
					label={i18nPackage.t("editQuizPage.liveSessionsStep.startTime")}
					error={
						registrationStartTimePastError
							? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.startTimePast")
							: registrationTimeOrderError
								? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.startTimeAfterEndTime")
								: registrationStartTimeAfterStartTimeError
									? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.startTimeAfterSessionStartTime")
									: unfilledRegistrationStartTimeError
										? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.startTimeRequired")
										: undefined
					}
				/>
				<VerticalLabel
					control={
						<CoreDatePicker
							disablePast
							value={registrationEndTime ?? null}
							onChange={(date) => {
								onChange({
									...session,
									registrationEndTime:
										date && registrationEndTime ? changeDateOnly(registrationEndTime, date) : (date ?? undefined),
									registrationStartTime:
										!registrationEndTime && date
											? stripSeconds(Date.now() + 10 * 60 * 1000)
											: session.registrationStartTime,
								});
							}}
							error={
								registrationEndDatePastError ||
								registrationDateOrderError ||
								registrationEndDateAfterEndDateError ||
								unfilledRegistrationEndTimeError ||
								registrationEndDateAfterCancellationDateError
							}
							dialogAriaLabel={i18nPackage.t(
								"editQuizPage.liveSessionsStep.registrationWindow.ariaLabels.endDateDialog",
							)}
							slotProps={{
								textField: {
									sx: {
										".MuiInputBase-input": {
											fontSize: 15,
										},
										"& .MuiInputBase-root": {
											height: dateTimeHeight,
										},
									},
								},
							}}
						/>
					}
					label={i18nPackage.t("editQuizPage.liveSessionsStep.endDate")}
					error={
						registrationEndDatePastError
							? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endDatePast")
							: registrationDateOrderError
								? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endDateBeforeStartDate")
								: registrationEndDateAfterEndDateError
									? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endDateAfterSessionEndDate")
									: unfilledRegistrationEndTimeError
										? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endDateRequired")
										: registrationEndDateAfterCancellationDateError
											? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endDateAfterCancellationTime", {
													count: Math.round(registrationDateAutoCancellationTimeDifference),
												})
											: undefined
					}
				/>
				<VerticalLabel
					control={
						<CoreTimePicker
							allowEmpty
							fontSize="15px"
							value={registrationEndTime ?? null}
							onChange={(time) => {
								onChange({
									...session,
									registrationEndTime: time,
									registrationStartTime:
										!registrationEndTime && time
											? stripSeconds(Date.now() + 10 * 60 * 1000)
											: session.registrationStartTime,
								});
							}}
							error={
								registrationEndTimePastError ||
								registrationTimeOrderError ||
								registrationEndTimeAfterEndTimeError ||
								unfilledRegistrationEndTimeError ||
								registrationEndTimeAfterCancellationTimeError
							}
							sx={{
								"& .MuiOutlinedInput-root": {
									height: dateTimeHeight,
								},
							}}
						/>
					}
					label={i18nPackage.t("editQuizPage.liveSessionsStep.endTime")}
					error={
						registrationEndTimePastError
							? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endTimePast")
							: registrationTimeOrderError
								? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endTimeBeforeStartTime")
								: registrationEndTimeAfterEndTimeError
									? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endTimeAfterSessionEndTime")
									: unfilledRegistrationEndTimeError
										? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endTimeRequired")
										: registrationEndTimeAfterCancellationTimeError
											? i18nPackage.t("editQuizPage.liveSessionsStep.registrationWindow.endTimeAfterCancellationTime", {
													count: proctorGroup?.autoCancelEmptyHoursBefore,
												})
											: undefined
					}
				/>
			</div>
		</div>
	);
}

function SessionEntry({
	availableProctors,
	availableProctorGroups,
	session,
	existingSession,
	onChange,
	expanded,
	setExpanded,
	onDelete,
	allowDemoSessions,
}: {
	availableProctors: Record<string, ProctorInfo>;
	availableProctorGroups: Record<number, ProctorGroupInfo>;
	session: PendingLiveSession;
	existingSession?: LiveSessionWithInvitations;
	expanded: boolean;
	setExpanded: (expanded: boolean) => void;
	onChange: (session: PendingLiveSession) => void;
	onDelete: () => void;
	allowDemoSessions: boolean;
}) {
	const { name, startTime, endTime } = session;
	const currTime = new Date();
	const isStarted = !!existingSession && new Date(existingSession.startTime) <= currTime;
	const isPast = existingSession && new Date(existingSession.endTime) <= currTime;
	const isMobileLayout = useMobileLayout();

	const nameChanged = !existingSession || isLiveSessionNameChanged(existingSession, session);
	const nameError = nameChanged && !name;

	const [menuShowing, setMenuShowing] = useState(false);

	const demoMode = isLiveSessionDemoMode(session);
	const setDemoMode = (enabled: boolean) => {
		if (enabled && !demoMode) {
			onChange({
				...session,
				name: `${name.trim()} (Demo)`,
				endTime:
					Math.abs(endTime.getTime() - startTime.getTime()) < 24 * 60 * 60 * 1000
						? new Date(startTime.getTime() + 365 * 24 * 60 * 60 * 1000)
						: endTime,
			});
		} else if (demoMode) {
			onChange({
				...session,
				name: name.replace(/\s*\(Demo\)\s*$/, ""),
				endTime:
					Math.abs(endTime.getTime() - startTime.getTime()) >= 24 * 60 * 60 * 1000
						? new Date(startTime.getTime() + 60 * 60 * 1000)
						: endTime,
			});
		}
	};

	const controls = (
		<div className={`${styles.sessionHeaderControls} ${isMobileLayout ? styles.sessionHeaderControlsCompact : ""}`}>
			<CoreMenuButton
				menuShowing={menuShowing}
				setMenuShowing={setMenuShowing}
				disabled={isStarted}
				tooltip={i18nPackage.t("editQuizPage.liveSessionsStep.actions")}
				disabledTooltip={i18nPackage.t("editQuizPage.liveSessionsStep.actionsDisabled")}
			>
				<CoreMenuItem
					onClick={() => {
						onDelete();
						setMenuShowing(false);
					}}
				>
					<ListItemText primary="Delete" />
				</CoreMenuItem>
				{allowDemoSessions && (
					<CoreMenuItem
						onClick={() => {
							setDemoMode(!demoMode);
							setMenuShowing(false);
						}}
					>
						<ListItemText primary={demoMode ? "Standard Session" : "Demo Session"} />
					</CoreMenuItem>
				)}
			</CoreMenuButton>
			<CoreExpandButton
				expanded={expanded}
				onChange={setExpanded}
				disabled={isPast}
				disabledTooltip={i18nPackage.t("editQuizPage.liveSessionsStep.expandDisabled")}
				collapsedTooltip={i18nPackage.t("editQuizPage.liveSessionsStep.expand")}
				expandedTooltip={i18nPackage.t("editQuizPage.liveSessionsStep.collapse")}
			/>
		</div>
	);

	const header = (
		<FormControlLabel
			className={`${styles.horizontalTextFieldLabel} ${isMobileLayout ? styles.horizontalTextFieldLabelCompact : ""}`}
			control={
				<CoreTextField
					className={styles.nameField}
					value={name}
					onChange={(e) => onChange({ ...session, name: e.target.value })}
					disabled={isStarted || isPast}
					disabledTooltip={isStarted ? "The Name cannot be edited because the session has started." : undefined}
					placeholder="Enter session name"
					error={nameError && "Enter a name for your session."}
					sx={{
						".MuiInputBase-input": { fontSize: "15px" },
						".MuiOutlinedInput-root": { margin: isMobileLayout ? "0px" : "2px" },
					}}
					fullWidth={isMobileLayout}
				/>
			}
			label={
				!isMobileLayout ? (
					"Name"
				) : (
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<span style={{ display: "flex", alignItems: "center" }}>Name</span>
						{controls}
					</div>
				)
			}
			labelPlacement={isMobileLayout ? "top" : "start"}
		/>
	);

	return (
		<li className={styles.sessionEntry}>
			<div className={`${styles.sessionHeader} ${isMobileLayout ? styles.sectionHeaderCompact : ""}`}>
				{expanded ? header : <SessionInfoHeader session={session} />}
				{(!isMobileLayout || !expanded) && controls}
			</div>
			{expanded && (
				<>
					<SessionEditor session={session} existingSession={existingSession} onChange={onChange} />
					<ProctorEditor
						availableProctors={availableProctors}
						availableProctorGroups={availableProctorGroups}
						session={session}
						existingSession={existingSession}
						onChange={onChange}
					/>
					<RegistrationWindowEditor
						availableProctorGroups={availableProctorGroups}
						session={session}
						existingSession={existingSession}
						onChange={onChange}
					/>
				</>
			)}
			<div style={{ display: expanded ? "contents" : "none" }}>
				<ProctorGuideEditor session={session} onChange={onChange} />
			</div>
		</li>
	);
}

function SessionsList({
	availableProctors,
	availableProctorGroups,
	existingSessions,
	pendingSessions,
	expandedSessionIds,
	setExpanded,
	onUpdate,
	onDelete,
	allowDemoSessions,
}: {
	availableProctors: Record<string, ProctorInfo>;
	availableProctorGroups: Record<number, ProctorGroupInfo>;
	existingSessions: Record<string, LiveSessionWithInvitations>;
	pendingSessions: PendingLiveSession[];
	expandedSessionIds: string[];
	setExpanded: (sessionId: string, expanded: boolean) => void;
	onUpdate: (sessionId: string, update: PendingLiveSessionUpdate) => void;
	onDelete: (id: string) => void;
	allowDemoSessions: boolean;
}) {
	return (
		<List className={styles.sessionsList}>
			{pendingSessions.map((session) => (
				<SessionEntry
					key={session.identifier}
					availableProctors={availableProctors}
					availableProctorGroups={availableProctorGroups}
					session={session}
					existingSession={session.id ? existingSessions[session.id] : undefined}
					onChange={(newSession) => onUpdate(session.identifier, newSession)}
					expanded={expandedSessionIds.includes(session.identifier)}
					setExpanded={(expanded) => setExpanded(session.identifier, expanded)}
					onDelete={() => onDelete(session.identifier)}
					allowDemoSessions={allowDemoSessions}
				/>
			))}
		</List>
	);
}

function ProctorGroupDeletionWarningDialog({
	onClose,
	message,
	confirmType,
	onConfirm,
}: {
	onClose: () => void;
	message: ReactNode;
	confirmType: "delete" | "confirm";
	onConfirm: () => void;
}) {
	const [confirmationChecked, setConfirmationChecked] = useState(false);
	return (
		<CoreModal
			open
			onClose={onClose}
			title={confirmType === "delete" ? "Delete Session" : "Confirm Changes"}
			buttons={
				<>
					<CoreButton color="secondary" onClick={onClose}>
						Cancel
					</CoreButton>
					<CoreButton
						color={confirmType === "delete" ? "cancel" : "primary"}
						disabled={!confirmationChecked}
						onClick={() => {
							onConfirm();
							onClose();
						}}
					>
						{confirmType === "delete" ? "Delete" : "Confirm"}
					</CoreButton>
				</>
			}
		>
			<Typography>{message}</Typography>
			<FormControlLabel
				className={styles.confirmationCheckboxLabel}
				labelPlacement="end"
				label="I understand and acknowledge the above statement."
				control={
					<CoreCheckbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} />
				}
			/>
		</CoreModal>
	);
}

function filterEditableSessionIds(currTime: Date, ids: string[], existingSessions: Record<string, LiveSession>) {
	return ids.filter((id) => {
		const existingSession = existingSessions[id];
		return !existingSession || new Date(existingSession.endTime) > currTime;
	});
}

export default function LiveSessionsStep({
	existingSessions,
	pendingSessionIds,
	setPendingSessionIds,
	pendingSessions,
	setPendingSessions,
	availableProctors,
	availableProctorGroups,
	getNextFakeLiveSessionId,
	nextConfirmationDialogMessage,
	cancelNextConfirmationDialog,
	confirmNextConfirmationDialog,
	allowDemoSessions,
}: {
	existingSessions: Record<string, LiveSessionWithInvitations>;
	pendingSessionIds: string[];
	setPendingSessionIds: (ids: string[]) => void;
	pendingSessions: Record<string, PendingLiveSession>;
	setPendingSessions: (sessions: Record<string, PendingLiveSession>) => void;
	availableProctors: Record<string, ProctorInfo>;
	availableProctorGroups: Record<string, ProctorGroupInfo>;
	getNextFakeLiveSessionId: () => string;
	nextConfirmationDialogMessage?: string;
	cancelNextConfirmationDialog: () => void;
	confirmNextConfirmationDialog: () => void;
	allowDemoSessions: boolean;
}) {
	const [currTime, setCurrTime] = useState(new Date());
	useEffect(() => {
		const interval = setInterval(() => setCurrTime(new Date()), 30 * 1000);
		return () => clearInterval(interval);
	}, []);

	const [deleteConfirmationDialogInfo, setDeleteConfirmationDialogInfo] = useState<
		| {
				id: string;
				proctorGroup: ProctorGroupInfo;
		  }
		| undefined
	>();

	const editablePendingSessionIds = filterEditableSessionIds(currTime, pendingSessionIds, existingSessions);
	const [expandedSessionIds, setExpandedSessionIds] = useState<string[]>(
		editablePendingSessionIds.length === 0 ? [] : [editablePendingSessionIds[editablePendingSessionIds.length - 1]],
	);
	const isMobileLayout = useMobileLayout();

	const getNextSessionNumber = () => {
		const regex = /^Session (\d+)\s*(?:\(Demo\))?\s*$/;
		return (
			Math.max.apply(
				null,
				Object.values(pendingSessions)
					.map(({ name }) => {
						const result = regex.exec(name);
						return result ? Number(result[1]) : 0;
					})
					.concat([0]),
			) + 1
		);
	};

	const addPendingSession = () => {
		const identifier = getNextFakeLiveSessionId();
		const currTimestamp = Date.now();
		setPendingSessionIds(pendingSessionIds.concat([identifier]));
		setPendingSessions({
			...pendingSessions,
			[identifier]: {
				identifier,
				name: `Session ${getNextSessionNumber()}`,
				startTime: stripSeconds(currTimestamp + 60 * 60 * 1000),
				endTime: stripSeconds(currTimestamp + 2 * 60 * 60 * 1000),
				capacity: 50,
				proctorId: Object.values(availableProctors)[0]?.id,
			},
		});
		setExpandedSessionIds([identifier]);
	};

	const updatePendingSession = (id: string, update: PendingLiveSessionUpdate) => {
		setPendingSessions(
			Object.fromEntries(
				Object.values(pendingSessions).map((session) => [
					session.identifier,
					session.identifier === id ? { ...session, ...update } : session,
				]),
			),
		);
	};

	const removePendingSession = (id: string) => {
		setPendingSessionIds(pendingSessionIds.filter((sessionId) => sessionId !== id));
		setPendingSessions(Object.fromEntries(Object.entries(pendingSessions).filter(([sessionId]) => sessionId !== id)));
	};

	const confirmRemovePendingSession = (id: string) => {
		const existingSession = existingSessions[id];
		const cancelledInvitations = existingSession
			? getPotentiallyCancelledInvitations(existingSession, availableProctorGroups)
			: [];
		if (cancelledInvitations.length > 0) {
			const proctorGroup = availableProctorGroups[cancelledInvitations[0].proctorGroupId];
			setDeleteConfirmationDialogInfo({ id, proctorGroup });
		} else {
			removePendingSession(id);
		}
	};

	useEffect(() => {
		const validIds = new Set(filterEditableSessionIds(currTime, pendingSessionIds, existingSessions));
		setExpandedSessionIds((expandedIds) => expandedIds.filter((id) => validIds.has(id)));
	}, [currTime, pendingSessionIds, existingSessions]);

	useEffect(() => {
		setPendingSessions(resetStartedLiveSessions(existingSessions, pendingSessions));
	}, [currTime, existingSessions]);

	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper component="section" className={styles.pageCompact} sx={{ padding: "20px 10px" }}>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => <div className={styles.page}>{children}</div>}
		>
			<p className={styles.description}>
				Create live assessment sessions that test-takers can register for by assigning them dates, times, and proctors.
			</p>
			<div className={styles.editor}>
				<Paper className={`${styles.headerCard} ${isMobileLayout ? styles.headerCardCompact : ""}`}>
					<Typography variant="h4" className={styles.headerTitle}>
						Create New Sessions
					</Typography>
					<CoreButton
						className={`${styles.addButton} ${isMobileLayout ? styles.addButtonCompact : ""}`}
						startIcon={
							<SvgIcon>
								<Add />
							</SvgIcon>
						}
						onClick={addPendingSession}
					>
						{isMobileLayout ? "Add" : "Add Session"}
					</CoreButton>
				</Paper>
				<SessionsList
					availableProctors={availableProctors}
					availableProctorGroups={availableProctorGroups}
					existingSessions={existingSessions}
					pendingSessions={pendingSessionIds.map((id) => pendingSessions[id])}
					expandedSessionIds={expandedSessionIds}
					setExpanded={(sessionId, expanded) => {
						const newExpandedSessions = expandedSessionIds.filter((id) => id !== sessionId);
						setExpandedSessionIds(expanded ? newExpandedSessions.concat([sessionId]) : newExpandedSessions);
					}}
					onUpdate={updatePendingSession}
					onDelete={confirmRemovePendingSession}
					allowDemoSessions={allowDemoSessions}
				/>
			</div>
			{deleteConfirmationDialogInfo && (
				<ProctorGroupDeletionWarningDialog
					onClose={() => setDeleteConfirmationDialogInfo(undefined)}
					confirmType="delete"
					message={deleteConfirmationDialogInfo.proctorGroup.trackCancellationMessage?.replaceAll(
						"{{sessionName}}",
						existingSessions[deleteConfirmationDialogInfo.id]?.name,
					)}
					onConfirm={() => removePendingSession(deleteConfirmationDialogInfo.id)}
				/>
			)}
			{nextConfirmationDialogMessage && (
				<ProctorGroupDeletionWarningDialog
					onClose={cancelNextConfirmationDialog}
					confirmType="confirm"
					message={nextConfirmationDialogMessage}
					onConfirm={confirmNextConfirmationDialog}
				/>
			)}
		</ConditionalWrapper>
	);
}
